import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, {
  formatApiError, INHERENT_LEVEL, tInherentLevel, tLikelihood, tImpact, tRiskConfidence,
  tTreatment, RISK_TREATMENT, RISK_TREATMENT_KEYS, INHERENT_LEVEL_KEYS,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EffBadge } from "@/components/shared/ControlsPanel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Gauge, ShieldCheck, Network, UserCheck, Loader2, ChevronDown, ChevronRight, Info, Lock,
} from "lucide-react";

function LevelBadge({ level, size = "sm" }) {
  const c = INHERENT_LEVEL[level] || INHERENT_LEVEL.LOW;
  const cls = size === "lg" ? "text-sm px-3 py-1" : "text-[10px]";
  return <Badge variant="outline" className={`rounded-sm font-mono ${cls} ${c.cls}`} data-testid="inherent-level-badge">{tInherentLevel(level)}</Badge>;
}

function Zone({ title, icon: Icon, testid, children, note }) {
  return (
    <div className="border border-border rounded-sm bg-background/40" data-testid={testid}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <Icon className="h-4 w-4 text-primary" />
        <h3 className="font-head font-semibold text-sm">{title}</h3>
      </div>
      <div className="p-4 space-y-3">
        {note && <p className="text-[11px] text-muted-foreground leading-snug">{note}</p>}
        {children}
      </div>
    </div>
  );
}

function InherentZone({ inherent, contributors }) {
  const [open, setOpen] = useState(false);
  return (
    <Zone title="Risque inhérent (calculé)" icon={Gauge} testid="risk-zone-inherent"
      note="Calculé AVANT contrôles et AVANT crédit de résilience (ADR D11c). Niveau ordinal — aucun score numérique global.">
      <div className="flex flex-wrap items-center gap-4">
        <LevelBadge level={inherent.level} size="lg" />
        <div className="text-xs font-mono text-muted-foreground">
          Vraisemblance <span className="text-foreground">{tLikelihood(inherent.likelihood)}</span>
          {" × "}Impact <span className="text-foreground">{tImpact(inherent.impact)}</span>
        </div>
        <div className="text-xs font-mono text-muted-foreground">Confiance <span className="text-foreground">{tRiskConfidence(inherent.confidence)}</span></div>
        <Badge variant="outline" className="rounded-sm text-[10px] font-mono text-muted-foreground">{inherent.contributor_count} contributeur(s)</Badge>
      </div>
      {inherent.contributor_count > 0 && (
        <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-1 text-xs text-primary" data-testid="toggle-risk-explain">
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Pourquoi ce niveau ?
        </button>
      )}
      {open && (
        <div className="space-y-3">
          <div className="border border-border rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead>Vulnérabilité</TableHead>
                  <TableHead>Actif</TableHead>
                  <TableHead>Vraisemblance</TableHead>
                  <TableHead>Niveau</TableHead>
                  <TableHead>Exposition</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(inherent.top_contributors || []).map((c, i) => (
                  <TableRow key={`${c.vulnerability_title}-${c.asset_name}`} className="border-border" data-testid={`risk-contributor-${i}`}>
                    <TableCell className="text-sm">{c.vulnerability_title}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{c.asset_name}</TableCell>
                    <TableCell className="text-xs font-mono">{tLikelihood(c.likelihood)}</TableCell>
                    <TableCell><LevelBadge level={c.level} /></TableCell>
                    <TableCell className="text-xs">{c.exposure_applied ? "Internet (+1)" : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <ul className="space-y-1">
            {(inherent.trace || []).map((t) => (
              <li key={`${t.rule_id}-${t.justification}`} className="text-[11px] text-muted-foreground font-mono">
                <span className="text-primary">[{t.rule_id}]</span> {t.justification}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Zone>
  );
}

function ResilienceZone({ ctx }) {
  return (
    <Zone title="Contexte de résilience structurelle" icon={Network} testid="risk-zone-resilience" note={ctx.note}>
      <div className="flex flex-wrap items-center gap-4 text-xs font-mono">
        <span>Fragilité structurelle : <span className="text-foreground">{ctx.fragility_level ?? "—"}</span></span>
        <span>SPOF : <span className="text-foreground">{(ctx.spofs || []).length}</span></span>
        <span>Redondances vérifiées : <span className="text-foreground">{ctx.verified_redundancies}</span></span>
        <span>Partielles : <span className="text-foreground">{ctx.partial_redundancies}</span></span>
      </div>
    </Zone>
  );
}

function ControlsContextZone({ ctx }) {
  return (
    <Zone title="Contrôles associés (efficacité créditable)" icon={ShieldCheck} testid="risk-zone-controls" note={ctx.note}>
      {(!ctx.controls || ctx.controls.length === 0) ? (
        <p className="text-sm text-muted-foreground">Aucun contrôle associé au service / à ses actifs / vulnérabilités.</p>
      ) : (
        <div className="space-y-2">
          <p className="text-[11px] font-mono text-muted-foreground">{ctx.creditable_count} contrôle(s) à efficacité créditable — <span className="text-yellow-400">non appliqué(s)</span> au risque inhérent.</p>
          {ctx.controls.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-3 border border-border rounded-sm px-3 py-2" data-testid={`risk-control-${c.id}`}>
              <span className="text-sm font-medium">{c.name}</span>
              <span className="flex items-center gap-2">
                <EffBadge eff={c.effectiveness} />
                {c.effectiveness?.creditable && <Badge variant="outline" className="rounded-sm text-[10px] font-mono text-green-400 border-green-500/30">créditable</Badge>}
              </span>
            </div>
          ))}
        </div>
      )}
    </Zone>
  );
}

function DecisionZone({ data, canAssess, canAccept, onDecision, onAccept, busy }) {
  const dec = data.decision;
  const ov = data.override;
  const acc = data.acceptance;
  const [owner, setOwner] = useState(dec?.owner || "");
  const [treatment, setTreatment] = useState(dec?.treatment || "OPEN");
  const [justification, setJustification] = useState(dec?.justification || "");
  const [dueDate, setDueDate] = useState(dec?.due_date || "");
  const [overrideLevel, setOverrideLevel] = useState("");
  const [overrideJust, setOverrideJust] = useState("");
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [accJust, setAccJust] = useState("");
  const [accReview, setAccReview] = useState("");

  const submitDecision = () => {
    onDecision({
      owner, treatment, justification, due_date: dueDate || null,
      overridden_level: overrideLevel || null, override_justification: overrideJust,
    });
  };

  return (
    <Zone title="Décision humaine" icon={UserCheck} testid="risk-zone-decision"
      note="La décision/l'override n'écrasent JAMAIS le calcul : ils sont attribués, horodatés, justifiés et audités.">
      {/* État courant */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="text-xs">
          <div className="text-muted-foreground uppercase tracking-wider text-[10px] font-mono mb-1">Traitement</div>
          <div data-testid="risk-decision-current">{dec ? `${tTreatment(dec.treatment)} — ${dec.owner || "sans propriétaire"}` : "Aucune décision"}</div>
          {dec?.updated_by && <div className="text-[10px] text-muted-foreground mt-1">par {dec.updated_by} · {(dec.updated_at || "").slice(0, 10)}</div>}
        </div>
        <div className="text-xs">
          <div className="text-muted-foreground uppercase tracking-wider text-[10px] font-mono mb-1">Override</div>
          {ov ? (
            <div data-testid="risk-override-current">
              Niveau forcé <LevelBadge level={ov.overridden_level} /> <span className="text-muted-foreground">(calculé : {tInherentLevel(ov.calculated_level_at_decision)})</span>
              <div className="text-[10px] text-muted-foreground mt-1">{ov.justification}</div>
            </div>
          ) : <span className="text-muted-foreground">Aucun override</span>}
        </div>
        <div className="text-xs">
          <div className="text-muted-foreground uppercase tracking-wider text-[10px] font-mono mb-1">Acceptation formelle</div>
          {acc ? (
            <div data-testid="risk-acceptance-current">
              Accepté par <span className="text-foreground">{acc.decided_by}</span>
              <div className="text-[10px] text-muted-foreground mt-1">revue : {acc.review_date} · niveau au moment : {tInherentLevel(acc.calculated_level_at_decision)}</div>
            </div>
          ) : <span className="text-muted-foreground">Non accepté</span>}
        </div>
      </div>

      {/* Formulaire (risk:assess) */}
      {canAssess ? (
        <div className="border-t border-border pt-3 mt-2 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label className="text-xs">Propriétaire</Label>
              <Input value={owner} onChange={(e) => setOwner(e.target.value)} className="mt-1 rounded-sm" data-testid="risk-owner-input" /></div>
            <div><Label className="text-xs">Traitement</Label>
              <Select value={treatment} onValueChange={setTreatment}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="risk-treatment-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RISK_TREATMENT_KEYS.map((k) => <SelectItem key={k} value={k}>{RISK_TREATMENT[k]}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="text-xs">Échéance de traitement</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1 rounded-sm" data-testid="risk-duedate-input" /></div>
            <div><Label className="text-xs">Justification</Label>
              <Input value={justification} onChange={(e) => setJustification(e.target.value)} className="mt-1 rounded-sm" data-testid="risk-justification-input" /></div>
          </div>
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">Override du niveau calculé (explicite, toujours justifié)</summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
              <div><Label className="text-xs">Niveau forcé</Label>
                <Select value={overrideLevel} onValueChange={setOverrideLevel}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="risk-override-select"><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>
                    {INHERENT_LEVEL_KEYS.map((k) => <SelectItem key={k} value={k}>{tInherentLevel(k)}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs">Justification de l'override</Label>
                <Input value={overrideJust} onChange={(e) => setOverrideJust(e.target.value)} className="mt-1 rounded-sm" data-testid="risk-override-justification-input" /></div>
            </div>
          </details>
          <div className="flex gap-2">
            <Button size="sm" className="rounded-sm gap-2" onClick={submitDecision} disabled={busy} data-testid="save-risk-decision-button">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer la décision
            </Button>
            {canAccept ? (
              <Button size="sm" variant="outline" className="rounded-sm gap-2" onClick={() => setAcceptOpen(true)} data-testid="open-accept-risk-button">
                <UserCheck className="h-4 w-4" /> Accepter le risque
              </Button>
            ) : (
              <span className="flex items-center gap-1 text-[11px] text-muted-foreground"><Lock className="h-3 w-3" /> Acceptation réservée au CISO (risk:accept)</span>
            )}
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground border-t border-border pt-3 mt-2 flex items-center gap-1"><Info className="h-3 w-3" /> Lecture seule — l'évaluation requiert la permission risk:assess.</p>
      )}

      <Dialog open={acceptOpen} onOpenChange={setAcceptOpen}>
        <DialogContent data-testid="accept-risk-dialog">
          <DialogHeader><DialogTitle>Acceptation formelle du risque</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Le niveau calculé au moment de la décision sera capturé. Justification et date de revue obligatoires.</p>
            <div><Label className="text-xs">Justification *</Label>
              <Textarea value={accJust} onChange={(e) => setAccJust(e.target.value)} className="mt-1 rounded-sm" data-testid="accept-justification-input" /></div>
            <div><Label className="text-xs">Date de revue *</Label>
              <Input type="date" value={accReview} onChange={(e) => setAccReview(e.target.value)} className="mt-1 rounded-sm" data-testid="accept-review-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setAcceptOpen(false)}>Annuler</Button>
            <Button className="rounded-sm" disabled={!accJust || !accReview || busy}
              onClick={() => { onAccept({ justification: accJust, review_date: accReview }); setAcceptOpen(false); }}
              data-testid="confirm-accept-risk-button">Confirmer l'acceptation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Zone>
  );
}

export default function RiskPanel({ serviceId }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const key = ["service-risk", serviceId];
  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get(`/services/${serviceId}/risk`)).data,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: key });
  const assessM = useMutation({
    mutationFn: async () => api.post(`/services/${serviceId}/risk/assess`),
    onSuccess: () => { toast.success("Risque réévalué"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const decisionM = useMutation({
    mutationFn: async (payload) => api.post(`/services/${serviceId}/risk/decision`, payload),
    onSuccess: () => { toast.success("Décision enregistrée"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const acceptM = useMutation({
    mutationFn: async (payload) => api.post(`/services/${serviceId}/risk/accept`, payload),
    onSuccess: () => { toast.success("Risque accepté"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const canAssess = can("risk:assess");
  const canAccept = can("risk:accept");
  const busy = decisionM.isPending || acceptM.isPending || assessM.isPending;

  return (
    <section className="border border-border bg-card rounded-sm" data-testid="section-risk">
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <h2 className="flex items-center gap-2 font-head font-semibold"><Gauge className="h-4 w-4 text-primary" /> Risque inhérent</h2>
        {canAssess && data && (
          <Button size="sm" variant="outline" className="rounded-sm gap-2" onClick={() => assessM.mutate()} disabled={busy} data-testid="reassess-risk-button">
            {assessM.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Recalculer
          </Button>
        )}
      </div>
      <div className="p-6 space-y-4">
        {isLoading || !data ? (
          <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Calcul du risque…</p>
        ) : (
          <>
            <InherentZone inherent={data.inherent} contributors={data.contributors} />
            <ResilienceZone ctx={data.resilience_context} />
            <ControlsContextZone ctx={data.controls_context} />
            <DecisionZone data={data} canAssess={canAssess} canAccept={canAccept} busy={busy}
              onDecision={(p) => decisionM.mutate(p)} onAccept={(p) => acceptM.mutate(p)} />
          </>
        )}
      </div>
    </section>
  );
}
